import { z } from 'zod';

import { supabase } from '../supabase';
import { fetchClientAccountIndex } from '../clientAccounts';
import type { ClientBadge } from '../../utils/clientStatus';
import type { FeatureClient } from '../../types/agile';
import { logError } from '../logger';

/**
 * The accounts of a backlog item, read from their single definition `v_feature_accounts`:
 * set by hand (`feature_clients`) or derived from the item's client requests. Every surface
 * (backlog list, item page, account pages, announcements, home) reads this — never
 * `feature_clients` alone, which misses the requesters (PLAN-DETTE 4.18).
 */
const rowSchema = z.object({
  feature_id: z.string(),
  account_id: z.string(),
  account_name: z.string(),
  account_website: z.string().nullable(),
  account_logo_url: z.string().nullable(),
  manual: z.boolean(),
  request_count: z.number(),
  priority_weight: z.number().nullable(),
  notes: z.string().nullable(),
  first_linked_at: z.string(),
  feature_client_id: z.string().nullable(),
});
export type FeatureAccountRow = z.infer<typeof rowSchema>;

const PAGE = 1000; // PostgREST max_rows: a larger .limit() silently truncates.
const COLUMNS = 'feature_id, account_id, account_name, account_website, account_logo_url, manual, ' +
  'request_count, priority_weight, notes, first_linked_at, feature_client_id';

type Filter = { featureId?: string; featureIds?: string[]; accountId?: string };

export async function fetchFeatureAccountRows(filter: Filter = {}): Promise<FeatureAccountRow[]> {
  const rows: FeatureAccountRow[] = [];
  for (let from = 0; ; from += PAGE) {
    let query = supabase.from('v_feature_accounts').select(COLUMNS).order('feature_id').order('account_id');
    if (filter.featureId) query = query.eq('feature_id', filter.featureId);
    if (filter.featureIds) query = query.in('feature_id', filter.featureIds);
    if (filter.accountId) query = query.eq('account_id', filter.accountId);
    const { data, error } = await query.range(from, from + PAGE - 1);
    if (error) throw new Error(`v_feature_accounts: ${error.message}`);
    rows.push(...z.array(rowSchema).parse(data ?? []));
    if ((data ?? []).length < PAGE) return rows;
  }
}

/** Domain shape of the backlog screens; `id` is the manual link id, or a stable derived key. */
export function toFeatureClient(row: FeatureAccountRow, index: Map<string, ClientBadge>): FeatureClient {
  const badge = index.get(row.account_id);
  const status: NonNullable<FeatureClient['account']>['status'] =
    badge?.kind === 'client' ? 'customer' : badge?.kind === 'former' ? 'churned' : 'prospect';
  return {
    id: row.feature_client_id ?? `${row.feature_id}:${row.account_id}`,
    feature_id: row.feature_id,
    account_id: row.account_id,
    notes: row.notes ?? undefined,
    priority_weight: row.priority_weight ?? 0,
    created_at: row.first_linked_at,
    updated_at: row.first_linked_at,
    manual: row.manual,
    request_count: row.request_count,
    account: {
      id: row.account_id,
      name: row.account_name,
      status,
      website: row.account_website ?? undefined,
      logo_url: row.account_logo_url ?? undefined,
    },
  };
}

export async function fetchFeatureClients(filter: Filter = {}): Promise<FeatureClient[]> {
  const [rows, index] = await Promise.all([fetchFeatureAccountRows(filter), fetchClientAccountIndex()]);
  return rows.map((row) => toFeatureClient(row, index));
}

/** Group clients by item, for list screens. */
export function groupByFeature(clients: FeatureClient[]): Record<string, FeatureClient[]> {
  const out: Record<string, FeatureClient[]> = {};
  for (const c of clients) (out[c.feature_id] ??= []).push(c);
  return out;
}

/** A backlog item of an account (account pages, brain panel), from `get_account_features`. */
export interface AccountFeatureRow {
  id: string;
  feature_id: string;
  account_id: string;
  notes: string | null;
  priority_weight: number;
  created_at: string;
  manual: boolean;
  request_count: number;
  feature: { id: string; title: string; dev_status: string; priority: string; category: string };
}

const accountFeatureSchema = z.object({
  feature_client_id: z.string().nullable(),
  feature_id: z.string(),
  account_id: z.string(),
  notes: z.string().nullable(),
  priority_weight: z.number().nullable(),
  linked_at: z.string(),
  manual: z.boolean(),
  request_count: z.number(),
  title: z.string(),
  dev_status: z.string().nullable(), // the column has no NOT NULL
  priority: z.string(),
  category: z.string(),
});

export async function fetchAccountFeatures(accountId: string, limit = 100): Promise<AccountFeatureRow[]> {
  const { data, error } = await supabase.rpc('get_account_features', { p_account_id: accountId, p_limit: limit });
  if (error) throw new Error(`get_account_features: ${error.message}`);
  return z.array(accountFeatureSchema).parse(data ?? []).map((r) => ({
    id: r.feature_client_id ?? `${r.feature_id}:${r.account_id}`,
    feature_id: r.feature_id,
    account_id: r.account_id,
    notes: r.notes,
    priority_weight: r.priority_weight ?? 0,
    created_at: r.linked_at,
    manual: r.manual,
    request_count: r.request_count,
    feature: { id: r.feature_id, title: r.title, dev_status: r.dev_status ?? '', priority: r.priority, category: r.category },
  }));
}

/** Same, for account pages where a missing block must not break the page: logs and returns []. */
export function fetchAccountFeaturesOrEmpty(accountId: string, limit = 100): Promise<AccountFeatureRow[]> {
  return fetchAccountFeatures(accountId, limit).catch((err: unknown) => {
    logError('backlog', 'account features unavailable', err, { account_id: accountId });
    return [];
  });
}
